import { RECURRING_TRANSFER_FREQUENCY_DESCRIPTION_MAP } from '@fruitful/types';
import * as React from 'react';
import { useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyLargeRegular,
    BodyMediumEmphasized,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    H2Regular,
    Icon,
    TallSheet,
    TrashIcon,
    useToastController,
    View,
    XStack,
} from 'smoothie';

import {
    centsToFormattedDollars,
    getRecurringTransferEndText,
} from '../../../../../common/utils/bankingUtils';
import {
    formatWithoutTimezone,
    SHORT_READABLE_DATE_FORMAT,
} from '../../../../../common/utils/dateUtils';
import { captureException } from '../../../../../logger/Logger';
import useCancelPaymentSchedule, {
    PatchPaymentScheduleStatus,
} from '../../../../hooks/useCancelPaymentSchedule';
import type { PaymentScheduleRenderDetails } from '../../../../hooks/useGetPaymentSchedules';
import { TokenizedAccountNumberDisclosure } from '../../../Disclosures';
import TransferAccountName from '../../TransferAccountName';
import { TransferAccountIcons } from '../../TransferIcons';

enum DetailsScreen {
    DETAILS = 'details',
    CONFIRM_DELETE = 'confirmDelete',
}

interface RecurringTransferDetailsSheetProps {
    onClose: () => void;
    transfer: PaymentScheduleRenderDetails;
    refetchTransfers: () => void;
}

const RecurringTransferDetailsSheet = ({
    onClose,
    transfer,
    refetchTransfers,
}: RecurringTransferDetailsSheetProps) => {
    const toast = useToastController();

    const [screen, setScreen] = useState(DetailsScreen.DETAILS);

    const {
        mutateAsync: cancelPaymentSchedule,
        isPending: isDeletingTransfer,
    } = useCancelPaymentSchedule();

    const deleteTransfer = async (transferId: string) => {
        try {
            await cancelPaymentSchedule({
                urlParams: {
                    paymentScheduleId: transferId,
                },
                body: {
                    status: PatchPaymentScheduleStatus.CANCELLED,
                },
            });

            refetchTransfers();
            onClose();

            toast.show('Recurring transfer deleted', {
                duration: 3000,
                icon: (
                    <View
                        backgroundColor="$red100"
                        padding={8}
                        borderRadius={4}
                    >
                        <Icon icon={TrashIcon} size={20} color="$red1000" />
                    </View>
                ),
            });
        } catch (e) {
            captureException(e);
            toast.show('Something went wrong');
        }
    };

    let title = 'Recurring transfer details';
    let content = (
        <RecurringTransferDetails
            transfer={transfer}
            onPressDelete={() => {
                setScreen(DetailsScreen.CONFIRM_DELETE);
            }}
        />
    );

    if (screen === DetailsScreen.CONFIRM_DELETE) {
        title = 'Are you sure?';
        content = (
            <ConfirmDelete
                onGoBack={() => {
                    setScreen(DetailsScreen.DETAILS);
                }}
                onConfirm={() => {
                    void deleteTransfer(transfer.id);
                }}
                transferDescription={transfer.recurringDescription}
                disabled={isDeletingTransfer}
            />
        );
    }

    return (
        <TallSheet
            isOpen={true}
            title={title}
            onClose={onClose}
            showCloseButton={true}
        >
            {content}
        </TallSheet>
    );
};

interface RecurringTransferDetailsProps {
    transfer: PaymentScheduleRenderDetails;
    onPressDelete: () => void;
}

const RecurringTransferDetails = ({
    transfer,
    onPressDelete,
}: RecurringTransferDetailsProps) => {
    const {
        fromAccount,
        toAccount,
        amount,
        recurringFrequency,
        recurringDescription,
        recurringStartDate,
        recurringEndType,
        recurringCount,
        recurringEndDate,
        metadata,
    } = transfer;

    return (
        <>
            <TransferAccountIcons
                fromAccount={fromAccount}
                toAccount={toAccount}
            />
            <H2Regular textAlign="center">
                {centsToFormattedDollars(amount)}
            </H2Regular>
            <BodyLargeRegular textAlign="center">
                Recurring{' '}
                {RECURRING_TRANSFER_FREQUENCY_DESCRIPTION_MAP[
                    recurringFrequency
                ].toLowerCase()}
            </BodyLargeRegular>
            <BodyLargeEmphasized textAlign="center" marginVertical={18}>
                {recurringDescription}
            </BodyLargeEmphasized>
            <TransferDetailRow
                label="From"
                value={<TransferAccountName depositAccount={fromAccount} />}
            />
            <TransferDetailRow
                label="To"
                value={<TransferAccountName depositAccount={toAccount} />}
            />
            <TransferDetailRow
                label="Repeats"
                value={
                    RECURRING_TRANSFER_FREQUENCY_DESCRIPTION_MAP[
                        recurringFrequency
                    ]
                }
            />
            <TransferDetailRow
                label="Started"
                value={formatWithoutTimezone(
                    recurringStartDate,
                    SHORT_READABLE_DATE_FORMAT,
                )}
            />
            <TransferDetailRow
                label="Ends"
                value={getRecurringTransferEndText(
                    recurringEndType,
                    recurringEndDate != null
                        ? formatWithoutTimezone(
                              recurringEndDate,
                              SHORT_READABLE_DATE_FORMAT,
                          )
                        : undefined,
                    recurringCount,
                )}
            />
            {!toAccount.isExternal && (
                <TransferDetailRow
                    label="Apply Income Split rules"
                    value={metadata?.applyIncomeSplit ? 'Yes' : 'No'}
                />
            )}
            {fromAccount.accountNumberMaskedIsTokenized &&
                fromAccount.nonTokenizedAccountNumberMasked != null && (
                    <XStack marginTop={8}>
                        <TokenizedAccountNumberDisclosure
                            account={fromAccount}
                        />
                    </XStack>
                )}
            {toAccount.accountNumberMaskedIsTokenized &&
                toAccount.nonTokenizedAccountNumberMasked != null && (
                    <XStack marginTop={8}>
                        <TokenizedAccountNumberDisclosure account={toAccount} />
                    </XStack>
                )}
            <Button
                buttonProps={{ marginTop: 20 }}
                variant={ButtonVariant.SECONDARY}
                tone="danger"
                size={ButtonSize.LARGE}
                onPress={onPressDelete}
                text="Delete"
                icon={TrashIcon}
            />
        </>
    );
};

interface TransferDetailRowProps {
    label: string;
    value: string | React.ReactNode;
}

const TransferDetailRow = ({ label, value }: TransferDetailRowProps) => {
    return (
        <XStack
            paddingVertical={10}
            borderBottomWidth={1}
            borderBottomColor="$gray100"
            justifyContent="space-between"
        >
            <BodyMediumRegular color="$gray1000">{label}</BodyMediumRegular>
            {typeof value === 'string' ? (
                <BodyMediumEmphasized
                    numberOfLines={2}
                    maxWidth={200}
                    textAlign="right"
                >
                    {value}
                </BodyMediumEmphasized>
            ) : (
                value
            )}
        </XStack>
    );
};

interface ConfirmDeleteProps {
    onGoBack: () => void;
    onConfirm: () => void;
    transferDescription: string;
    disabled: boolean;
}

const ConfirmDelete = ({
    onGoBack,
    onConfirm,
    transferDescription,
    disabled,
}: ConfirmDeleteProps) => {
    return (
        <>
            <BodyMediumRegular textAlign="center" marginVertical={12}>
                All future transfers related to “{transferDescription}” will be
                cancelled.
            </BodyMediumRegular>
            <Button
                variant={ButtonVariant.TERTIARY}
                size={ButtonSize.LARGE}
                text="Go back"
                onPress={onGoBack}
            />
            <Button
                variant={ButtonVariant.PRIMARY}
                tone="danger"
                size={ButtonSize.LARGE}
                text="Delete"
                onPress={onConfirm}
                disabled={disabled}
            />
        </>
    );
};

export default RecurringTransferDetailsSheet;
